<template>
  <div class="kpi-rating">
    <div class="kpi-rating__info">
      <h3 class="kpi-rating__headline">{{ label }}</h3>
      <p class="kpi-rating__absolute-change">
        <AnimatedNumber :value="value" />
        {{ unit }}
      </p>

      <p v-if="percentualChange" class="kpi-rating__percentual-change">
        <span
          :class="{
            'kpi-rating--above-average': percentualChange.value < 0,
            'kpi-rating--below-average': percentualChange.value > 0,
            'kpi-rating--same-as-average': percentualChange.value === 0,
          }"
        >
          {{ percentualChange.sign }}
          <AnimatedNumber
            class="kpi-rating__percentual-change-value"
            :value="Math.abs(percentualChange.value)"
          />{{ $t('ui.symbol.percentage') }}
        </span>
        {{ percentualChange.label }}
      </p>
    </div>
    <component :is="sdgComponent" class="kpi-rating__image" />
  </div>
</template>

<script>
import AnimatedNumber from '../AnimatedNumber';

import Goal3 from '../../assets/images/sustainableDevelopmentGoals/goal_03.svg?inline';
import Goal4 from '../../assets/images/sustainableDevelopmentGoals/goal_04.svg?inline';
import Goal7 from '../../assets/images/sustainableDevelopmentGoals/goal_07.svg?inline';
import Goal8 from '../../assets/images/sustainableDevelopmentGoals/goal_08.svg?inline';
import Goal9 from '../../assets/images/sustainableDevelopmentGoals/goal_09.svg?inline';
import Goal12 from '../../assets/images/sustainableDevelopmentGoals/goal_12.svg?inline';
import Goal13 from '../../assets/images/sustainableDevelopmentGoals/goal_13.svg?inline';

const componentMapping = {
  co2: 'Goal13',
  energy: 'Goal7',
  plastic: 'Goal12',
  revenue: 'Goal8',
  innovation: 'Goal9',
  customerSatisfaction: 'Goal9',
  employeeHappiness: 'Goal8',
  healthProgram: 'Goal3',
  training: 'Goal4',
};

export default {
  name: 'KpiRating',

  components: {
    Goal3,
    Goal4,
    Goal7,
    Goal8,
    Goal9,
    Goal12,
    Goal13,
    AnimatedNumber,
  },

  props: {
    label: {
      type: String,
      required: true,
    },
    unitIsReversed: {
      type: Boolean,
      required: true,
    },
    value: {
      type: Number,
      required: true,
    },
    unit: {
      type: String,
      required: true,
    },
    averageValue: {
      type: Number,
      required: true,
    },
    sdgId: {
      type: String,
      required: true,
    },
  },

  computed: {
    sdgComponent() {
      const component = componentMapping[this.sdgId];
      if (!component) return;

      return component;
    },

    percentualChange() {
      const value = this.averageValue * 100;
      let result = { value };
      if (value < 0) {
        result = {
          sign: this.unitIsReversed ? '+' : '-',
          value,
          label: this.$t('kpi.ratingChange.belowAverage'),
        };
      }
      if (value > 0) {
        result = {
          sign: this.unitIsReversed ? '-' : '+',
          value,
          label: this.$t('kpi.ratingChange.aboveAverage'),
        };
      }
      if (value === 0) {
        result = {
          sign: '',
          value,
          label: this.$t('kpi.ratingChange.aboveAverage'),
        };
      }

      return result;
    },
  },
};
</script>

<style lang="scss" scoped>
$image-width: 75px;

.kpi-rating {
  display: grid;
  justify-content: space-between;
  grid-template-columns: auto $image-width;
  grid-column-gap: 20px;
}

.kpi-rating__info {
  display: flex;
  flex-direction: column;
}

.kpi-rating__headline {
  font-size: 18px;
  margin-bottom: 8px;
}

.kpi-rating__absolute-change {
  margin-top: auto;
}

.kpi-rating__percentual-change {
  font-size: 14px;
}

.kpi-rating__image {
  grid-column: 2;
  width: $image-width;
}

.kpi-rating--above-average {
  color: $color-purple;
}

.kpi-rating--below-average {
  color: $color-green;
}
</style>
